import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Tooltip,
  Checkbox,
  Divider,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Block,
  QuestionMark,
  ChevronLeft,
  ChevronRight,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelCircleIcon,
  Error as ErrorIcon,
  RemoveCircle as RemoveCircleIcon,
  Inventory as InventoryIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  LocalPharmacy as LocalPharmacyIcon,
  FiberManualRecord,
  Event as EventIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SignatureInput from "../components/SignatureInput";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isFuture,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { formatToUIDate, formatToBackendDateTime } from "../utils/dateUtils";
import OrderMedicationService from "../services/OrderMedicationService";
import residentService from "../services/residentService";
import clinicalNoteService from "../services/clinicalNoteService";
import { MonthlyMARRecord, MonthlyMARSlot, MonthlyMARDose } from "../types/medication.types";
import { useAuth } from "../contexts/AuthContext";

const STAT_REGISTRY_KEY = "qt-carex-stat-dose-medications";

const normalizeMedName = (value: any): string => String(value ?? "").trim().toLowerCase();

const isStatDoseSource = (entity: any): boolean => {
  if (!entity || typeof entity !== "object") return false;

  const sourceLike = String(
    entity.source ??
    entity.medicationSource ??
    entity.orderSource ??
    entity.type ??
    entity.orderType ??
    entity.medicationType ??
    "",
  ).toLowerCase();

  const scheduleLike = String(
    entity.schedule ??
    entity.frequency ??
    entity.reason ??
    "",
  ).trim().toLowerCase();

  return Boolean(
    entity.isStatDose === true ||
    entity.statDose === true ||
    entity.isStat === true ||
    sourceLike === "stat_dose" ||
    sourceLike === "stat-dose" ||
    sourceLike === "statdose" ||
    scheduleLike === "stat" ||
    scheduleLike === "stat dose" ||
    scheduleLike === "stat_dose" ||
    scheduleLike === "stat-dose",
  );
};

interface MedicationAdministrationRecordProps {
  residentId: string;
  onClose?: () => void;
  onSaveSuccess?: () => void;
  refreshTrigger?: number;
  showResidentName?: boolean;
  sx?: any;
  readOnly?: boolean;
  medicationDefinitions?: any[];
}

const MedicationAdministrationRecord: React.FC<
  MedicationAdministrationRecordProps
> = ({
  residentId,
  onClose,
  onSaveSuccess,
  refreshTrigger,
  showResidentName = true,
  sx,
  readOnly = false,
  medicationDefinitions = [],
}) => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [medicationRecords, setMedicationRecords] = useState<
      MonthlyMARRecord[]
    >([]);
    const [nurseNotes, setNurseNotes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [residentName, setResidentName] = useState("");
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [selectedCell, setSelectedCell] = useState<{
      record: MonthlyMARRecord;
      slot: MonthlyMARSlot;
      day: Date;
      doseData?: MonthlyMARDose;
    } | null>(null);
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [resultsResponse, setResultsResponse] = useState("");
    const [administrationTime, setAdministrationTime] = useState(format(new Date(), "HH:mm"));
    const [dialogStatus, setDialogStatus] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [pendingStatus, setPendingStatus] = useState<string | null>(null);

    const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();

    // Aggressive catch-all check for any property containing 'narcotic'
    const checkNarcotic = (obj: any) => {
      if (!obj) return false;
      return Object.keys(obj).some(key => {
        const k = key.toLowerCase();
        return (k.includes('narcotic') || k.includes('norcatic')) &&
          (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
      });
    };

    // Aggressive catch-all check for any property containing 'critical'
    const checkCritical = (obj: any) => {
      if (!obj) return false;
      return Object.keys(obj).some(key => {
        const k = key.toLowerCase();
        return k.includes('critical') &&
          (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
      });
    };

    // Inline Signature States
    const [signatureImage, setSignatureImage] = useState<string | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [signatureTab, setSignatureTab] = useState(0); // 0 for upload, 1 for draw
    const [signatureFile, setSignatureFile] = useState<Blob | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success" as "success" | "error",
    });
    const [statMedications, setStatMedications] = useState<Set<string>>(new Set());
    const [narcoticMedNames, setNarcoticMedNames] = useState<Set<string>>(new Set());
    const [criticalMedNames, setCriticalMedNames] = useState<Set<string>>(new Set());
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    const dataURLtoFile = (dataurl: string, filename: string) => {
      const arr = dataurl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : '';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    const handleSignatureSave = async (signatureDataUrl: string) => {
      if (pendingStatus) {
        const file = dataURLtoFile(signatureDataUrl, 'signature.png');
        await submitRecord(pendingStatus, file);
        setSignatureModalOpen(false);
      }
    };

    const readStatMedicationNames = (residentKey: string) => {
      try {
        const raw = localStorage.getItem(STAT_REGISTRY_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const keys = [`resident::${residentKey}`, `unknown::${residentKey}`];
        const names = keys.flatMap((k) => (parsed && parsed[k]) || []);
        return new Set((names || []).map(normalizeMedName).filter(Boolean));
      } catch {
        return new Set<string>();
      }
    };

    const isStatMedication = (record: any) =>
      isStatDoseSource(record) || statMedications.has(normalizeMedName(record?.medicationName));

    const isNarcoticMedication = (record: any) => {
      if (!record) return false;
      // Direct flag on the MAR record itself
      if (record.isNarcotic === true || record.isNarcotic === "true") return true;
      // Pre-scanned set from medicationDefinitions
      if (narcoticMedNames.has(normalize(record.medicationName || ""))) return true;
      // Aggressive key-scan fallback
      return checkNarcotic(record);
    };

    const isCriticalMedication = (record: any) => {
      if (!record) return false;
      // Direct flag on the MAR record itself
      if (record.isCritical === true || record.isCritical === "true") return true;
      // Pre-scanned set from medicationDefinitions
      if (criticalMedNames.has(normalize(record.medicationName || ""))) return true;
      // Aggressive key-scan fallback
      return checkCritical(record);
    };

    useEffect(() => {
      if (residentId) {
        setStatMedications(readStatMedicationNames(residentId));
        fetchData();
      }
    }, [residentId, currentDate, refreshTrigger]);

    // Pre-calculate narcotic medications from definitions for reliable lookup
    useEffect(() => {
      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const names = new Set<string>();

      medicationDefinitions.forEach(m => {
        const isNarc = !!(m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || checkNarcotic(m));
        if (isNarc) {
          const normalizedName = normalize(m.medicationName || m.name);
          if (normalizedName) names.add(normalizedName);
        }
      });

      console.log("MAR: Pre-scanned narcotics count:", names.size, Array.from(names));
      setNarcoticMedNames(names);
    }, [medicationDefinitions]);

    // Pre-calculate critical medications from definitions for reliable lookup
    useEffect(() => {
      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const names = new Set<string>();

      medicationDefinitions.forEach(m => {
        const isCrit = !!(m.isCritical || m.is_critical || checkCritical(m));
        if (isCrit) {
          const normalizedName = normalize(m.medicationName || m.name);
          if (normalizedName) names.add(normalizedName);
        }
      });

      console.log("MAR: Pre-scanned critical medications count:", names.size, Array.from(names));
      setCriticalMedNames(names);
    }, [medicationDefinitions]);

    // Signature Helpers
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      setIsDrawing(true);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (!isDrawing) return;
      setIsDrawing(false);

      // Save canvas as blob
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.toBlob((blob: Blob | null) => {
          setSignatureFile(blob);
          setSignatureImage(canvas.toDataURL());
        }, 'image/png');
      }
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureImage(null);
      setSignatureFile(null);
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSignatureFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSignatureImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        // We need the facilityId to call the MAR API.
        // Assuming residentService can provide the resident details which contains facilityId.
        const resident = await residentService.getResidentById(residentId);
        const facilityId = resident.facilityId;
        setResidentName(residentService.getFullName(resident));

        if (!facilityId) {
          console.error("Facility ID not found for resident");
          setLoading(false);
          return;
        }

        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        // Format dates for API
        const startDateStr = format(start, "yyyy-MM-dd");
        const endDateStr = format(end, "yyyy-MM-dd");

        // Updated to use the correct GET API for monthly statuses
        const response = await OrderMedicationService.getMonthlyMAR(
          facilityId,
          residentId,
          startDateStr,
          endDateStr,
        );
        setMedicationRecords(response);

        const notes = await clinicalNoteService.getNurseNotesByResident(residentId);
        setNurseNotes(notes || []);
      } catch (error) {
        console.error("Failed to fetch MAR data", error);
      } finally {
        setLoading(false);
      }
    };

    const handleLowStockToggle = async (record: MonthlyMARRecord, checked: boolean) => {
      try {
        const now = new Date();
        const slotTime = record.doses?.[0]?.slotTime || "";

        let existingNoteId: string | undefined = undefined;

        // Find if there's already a note for this medication today and this slot
        const existingNote = nurseNotes.find((n: any) => {
          if (!n.dateTime) return false;
          const d = new Date(n.dateTime);
          const isSameDay = d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate();

          if (n.medicationName !== record.medicationName) return false;
          if (!isSameDay) return false;

          if (n.slotTime && slotTime) {
            return n.slotTime.startsWith(slotTime.substring(0, 5));
          }
          return false;
        });

        existingNoteId = existingNote?.id;

        const payload = {
          residentId: residentId,
          medicationName: record.medicationName,
          dosage: record.dose || "N/A",
          dateTime: formatToBackendDateTime(now) || "",
          slotTime: slotTime,
          medicationStatus: existingNote?.medicationStatus || "Note",
          reason: record.schedule || "Stock Update",
          schedule: record.schedule || "Stock Update",
          result: checked ? "Marked as Low Stock" : "Marked as In Stock",
          lowStock: checked,
          updatedStock: record.updatedStock ?? (record as any).updated_stock ?? "N/A"
        };

        if (existingNoteId) {
          await clinicalNoteService.updateNurseNote(existingNoteId, payload);
        } else {
          await clinicalNoteService.createNurseNote(payload);
        }

        // Refresh nurse notes to update the UI
        const notes = await clinicalNoteService.getNurseNotesByResident(residentId);
        setNurseNotes(notes || []);
        if (onSaveSuccess) onSaveSuccess();
      } catch (error) {
        console.error("Failed to update low stock status", error);
      }
    };

    const handleCellClick = (record: MonthlyMARRecord, slot: MonthlyMARSlot, day: Date, doseData?: MonthlyMARDose) => {
      if (readOnly) return;
      const isPastDay = isBefore(day, startOfDay(new Date()));
      const isFutureDay = isFuture(day) && !isToday(day);

      if (!doseData || isFutureDay || doseData.status === "Taken") return;

      if (isPastDay) {
        const allowedRoles = ["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "OFFICER"];
        if (!user?.role || !allowedRoles.includes(user.role)) {
          setSnackbar({ open: true, message: "You do not have access to edit previous medication. Please contact your Facility Manager.", severity: "error" });
          return;
        }
      }

      setSelectedCell({ record, slot, day, doseData });
      const initialNote = doseData?.notes || "";
      setResultsResponse((initialNote.startsWith("Administered as ") || initialNote.startsWith("Scheduled Dose")) ? "" : initialNote);
      // Default to the current real-world time for the administration entry
      const defaultTime = format(new Date(), "HH:mm");
      setAdministrationTime(defaultTime);
      setDialogStatus(doseData?.status || null);
      setIsStatusDialogOpen(true);
    };


    const checkIsReasonMandatory = (
      day: Date | string | null | undefined,
      slotTimeStr?: string | null,
      dateTimeStr?: string | null
    ): boolean => {
      if (!day) return false;
      const targetDate = typeof day === "string" ? new Date(day) : new Date(day.getTime());
      if (isNaN(targetDate.getTime())) return false;

      const now = new Date();
      const todayStart = startOfDay(now);
      const cellDayStart = startOfDay(targetDate);

      // 1. Past date -> Reason mandatory for any authorized user
      if (isBefore(cellDayStart, todayStart)) {
        return true;
      }

      // 2. Today -> check if past active window
      if (cellDayStart.getTime() === todayStart.getTime()) {
        let effectiveSlotTime = slotTimeStr;
        if (!effectiveSlotTime && dateTimeStr) {
          try {
            const dt = new Date(dateTimeStr);
            if (!isNaN(dt.getTime())) {
              effectiveSlotTime = format(dt, "HH:mm");
            }
          } catch {
            // ignore
          }
        }

        if (!effectiveSlotTime) return false;

        try {
          let hours = 0;
          let minutes = 0;

          const upper = effectiveSlotTime.trim().toUpperCase();
          const isPM = upper.includes("PM");
          const isAM = upper.includes("AM");
          const cleanTime = upper.replace(/(AM|PM)/g, "").trim();
          const parts = cleanTime.split(":").map(Number);

          if (parts.length >= 1 && !isNaN(parts[0])) {
            hours = parts[0];
            minutes = parts.length >= 2 && !isNaN(parts[1]) ? parts[1] : 0;
            if (isPM && hours < 12) hours += 12;
            if (isAM && hours === 12) hours = 0;
          }

          const slotDateTime = new Date();
          slotDateTime.setHours(hours, minutes, 0, 0);

          // If scheduled time has not arrived yet, it's on-time / upcoming
          if (now <= slotDateTime) {
            return false;
          }

          // Scheduled time HAS passed. Check active window end.
          let windowEndHour = hours + 2; // fallback 2-hour buffer
          if (hours >= 6 && hours < 11) windowEndHour = 11;
          else if (hours >= 11 && hours < 15) windowEndHour = 15;
          else if (hours >= 15 && hours < 19) windowEndHour = 19;
          else if (hours >= 19 || hours < 6) windowEndHour = 24;

          const currentHourFloat = now.getHours() + (now.getMinutes() / 60);

          // If current time is past active window end -> Late -> Mandatory Reason
          if (currentHourFloat >= windowEndHour) {
            return true;
          }
        } catch (e) {
          console.error("Error evaluating active window:", e);
        }
      }

      return false;
    };

    const handleRecordStatus = async (status: string) => {
      if (!selectedCell) return;
      setFormError(null);

      const isReasonReq = checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime);
      if ((isReasonReq || status === 'Unable' || status === 'Missed' || status === 'NotTaken') && !resultsResponse.trim()) {
        setFormError("Reason is mandatory for late or past-date medication administration.");
        return;
      }

      const r = selectedCell.record as any || {};
      const d = selectedCell.doseData as any || {};

      const normalize = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").trim();
      const medNameNormalized = normalize(r.medicationName);

      // 1. Direct flags on record or dose
      const hasDirectFlag = !!(
        r.isNarcotic || r.is_narcotic || r.isNorcatiik || r.isNorcatick ||
        d.isNarcotic || d.is_narcotic || d.isNorcatiik || d.isNorcatick ||
        checkNarcotic(r) || checkNarcotic(d)
      );

      // 2. Lookup in our pre-scanned narcotic list (Most reliable)
      const isNarcoticInList = medNameNormalized ? narcoticMedNames.has(medNameNormalized) : false;

      const isNarcoticMed = hasDirectFlag || isNarcoticInList;

      console.log("MAR DETECTOR DEBUG:", {
        medName: r.medicationName,
        normalized: medNameNormalized,
        isNarcoticMed,
        hasDirectFlag,
        isNarcoticInList,
        scannedCount: narcoticMedNames.size
      });

      if (status === "Taken" && isNarcoticMed) {
        setPendingStatus(status);
        console.log("STOP: Narcotic detected. Opening inline signature box. No API called yet.");
        return; // ABSOLUTE RETURN - Stop here and show signature UI
      } else if (status === "Taken" && !isNarcoticMed) {
        console.warn("PROCEED: No narcotic flag found. Calling API directly.");
      }

      // ONLY for non-narcotic or non-Taken status do we call the API
      await submitRecord(status);
      setIsStatusDialogOpen(false);
    };

    const submitRecord = async (status: string, signatureFile?: Blob) => {
      if (!selectedCell) return;

      const isReasonReq = checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime);
      if ((isReasonReq || status === 'Unable' || status === 'Missed' || status === 'NotTaken') && !resultsResponse.trim()) {
        setFormError("Reason is mandatory for late or past-date medication administration.");
        return;
      }

      setIsSavingStatus(true);
      try {
        // Build dateTime using the selected day and the administrationTime from the dialog
        const [hours, minutes] = administrationTime.split(":").map(Number);
        const administrationDateTime = new Date(selectedCell.day);
        administrationDateTime.setHours(hours, minutes, 0, 0);

        const backendStatus =
          status === "Missed" || status === "NotTaken" ? "NotTaken" :
            status === "Out of Stock" || status === "OutOfMeds" || status === "OutOfStock" ? "OutOfStock" :
                status;

        // Ensure slotTime is in valid format like "07:00" (HH:mm)
        let formattedSlotTime = selectedCell.slot.slotTime || "";
        if (formattedSlotTime.includes("AM") || formattedSlotTime.includes("PM")) {
          try {
            const [time, period] = formattedSlotTime.split(" ");
            let [h, m] = time.split(":").map(Number);
            if (period === "PM" && h < 12) h += 12;
            if (period === "AM" && h === 12) h = 0;
            formattedSlotTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          } catch (e) {
            console.error("Failed to parse slotTime", formattedSlotTime);
          }
        } else if (formattedSlotTime.includes(":")) {
          // If it has seconds, remove them to match HH:mm requirement
          formattedSlotTime = formattedSlotTime.split(':').slice(0, 2).join(':');
        }

        const checkNarcotic = (obj: any) => {
          if (!obj) return false;
          return Object.keys(obj).some(key => {
            const k = key.toLowerCase();
            return (k.includes('narcotic') || k.includes('norcatic')) &&
              (obj[key] === true || obj[key] === "true" || obj[key] === 1 || obj[key] === "1");
          });
        };

        const r = selectedCell.record as any || {};
        const d = (selectedCell.doseData as any) || {};

        const medName = (r.medicationName || "").toLowerCase().trim();

        const hasDirectFlag = !!(
          r.isNarcotic || r.isNorcatiik || r.isNorcatick || r.is_narcotic ||
          d.isNarcotic || d.isNorcatiik || d.isNorcatick || d.is_narcotic ||
          checkNarcotic(r) || checkNarcotic(d)
        );

        const isNarcoticByName = medicationDefinitions.some(m => {
          const mName = (m.medicationName || "").toLowerCase().trim();
          return mName === medName && (m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || checkNarcotic(m));
        });

        const isNarcoticMed = hasDirectFlag || isNarcoticByName;

        const payload = {
          residentId: residentId,
          medicationName: selectedCell.record.medicationName,
          dosage: selectedCell.record.dose || "N/A",
          dateTime: format(administrationDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          slotTime: formattedSlotTime,
          medicationStatus: backendStatus,
          reason: resultsResponse.trim() || selectedCell.record.schedule || "Scheduled Dose",
          schedule: selectedCell.record.schedule || "Scheduled Dose",
          result: resultsResponse,
          lowStock: false,
          updatedStock: selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock,
          updatedByName: user?.name || user?.username || "-",
        };

        const noteIdToUpdate = selectedCell.doseData?.id || selectedCell.doseData?.apiId;
        let existingNoteId = noteIdToUpdate;

        if (!existingNoteId) {
          // Robust matching logic matching MedicationSchedulePage
          const targetTime = selectedCell.day;
          const targetSlot = formattedSlotTime;

          const existingNote = nurseNotes.find((n: any) => {
            if (!n.dateTime) return false;
            const d = new Date(n.dateTime);
            const isSameFullDate = d.getFullYear() === targetTime.getFullYear() &&
              d.getMonth() === targetTime.getMonth() &&
              d.getDate() === targetTime.getDate();

            if (n.medicationName !== selectedCell.record.medicationName) return false;
            if (!isSameFullDate) return false;

            // Priority 1: Match by slotTime (the best unique indicator for a medication on a given day)
            if (n.slotTime && targetSlot) {
              return n.slotTime.startsWith(targetSlot.substring(0, 5));
            }

            // Priority 2: fallback to approximate time if no slotTime
            return d.getHours() === administrationDateTime.getHours() &&
              d.getMinutes() === administrationDateTime.getMinutes();
          });
          existingNoteId = existingNote?.id;
        }

        if (existingNoteId) {
          await clinicalNoteService.updateNurseNote(existingNoteId, payload, signatureFile);
        } else {
          await clinicalNoteService.createNurseNote(payload, signatureFile);
        }

        setSnackbar({ open: true, message: "Medication status recorded successfully", severity: "success" });
        await fetchData();
        if (onSaveSuccess) onSaveSuccess();
        setIsStatusDialogOpen(false);
        setSelectedCell(null);
        setResultsResponse("");
        setPendingStatus(null);
      } catch (error: any) {
        setIsSavingStatus(false);
        console.error("Failed to record medication status", error);

        // Handle Narcotic Signature Required Error from Backend
        const errorMsgRaw = error.response?.data?.message || "";
        const errorCode = error.response?.data?.errorCode || "";

        if (
          errorCode === "BUSINESS_VALIDATION_ERROR" &&
          (errorMsgRaw.toLowerCase().includes("signature") ||
            errorMsgRaw.toLowerCase().includes("narcotic") ||
            errorMsgRaw.toLowerCase().includes("sign"))
        ) {
          setPendingStatus(status);
          setFormError("Signature is mandatory for narcotic medications. Please sign below.");
          // Do not close isStatusDialogOpen here; we want the user to see the inline signature UI!
          return;
        }

        const errorMsg = error.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK"
          ? "Stock Not Available For This Medication. (Contact Facility Manager)"
          : (error.response?.data?.message || error.message || "Failed to record status");
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      } finally {
        setIsSavingStatus(false);
      }
    };

    const daysInMonth = eachDayOfInterval({
      start: startOfMonth(currentDate),
      end: endOfMonth(currentDate),
    });

    // Helper to find a dose record for a specific slot and day
    const findDoseInSlot = (
      slot: MonthlyMARSlot,
      day: Date,
    ): MonthlyMARDose | undefined => {
      const dateStrISO = format(day, "yyyy-MM-dd");
      const dateStrUK = format(day, "dd/MM/yyyy");
      const dateStrUS = format(day, "MM/dd/yyyy");

      const found = slot.records.find((r: MonthlyMARDose) => {
        if (r.date === dateStrISO) return true;
        if (r.dateTime) {
          // Accept ISO format: "2026-01-03T07:00:00"
          if (r.dateTime.startsWith(dateStrISO)) return true;
          // Accept UK format: "03/01/2026 07:00"
          if (r.dateTime.startsWith(dateStrUK)) return true;
          // Accept US format: "01/03/2026 07:00"
          if (r.dateTime.startsWith(dateStrUS)) return true;
        }
        return false;
      });

      if (found) {
        let mappedStatus = found.status;
        if (found.status === "PENDING") mappedStatus = "Upcoming";
        else if (found.status === "NotTaken") mappedStatus = "Missed";
        else if (found.status === "OutOfStock" || found.status === "OutOfMeds") mappedStatus = "Out of Stock";

        return {
          ...found,
          status: mappedStatus
        };
      }
      return undefined;
    };

    // Refined Icon Renderer based on Image
    const renderStatusIcon = (status: string, colorCode?: string) => {
      const boxStyle = {
        width: 28,
        height: 28,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        bgcolor: colorCode || "#546e7a", // Fallback color
      };

      switch (status) {
        case "Taken":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#D1FAE5", color: "#10B981" }}>
              <CheckIcon fontSize="small" />
            </Box>
          );
        case "NotTaken":
        case "Missed":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#FEE2E2", color: "#EF4444" }}>
              <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Refused":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#fce4ec", color: "#f06292" }}>
              <Block fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Upcoming":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#EDE9FE", color: "#8B5CF6" }}>
              <HistoryIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "OutOfMeds":
        case "Out of Stock":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#F3F4F6", color: "#6B7280" }}>
              <InventoryIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "Discontinued":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#FFEDD5", color: "#8B4513" }}>
              <RemoveCircleIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "PRN":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#E0F2FE", color: "#0EA5E9" }}>
              <LocalPharmacyIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        // Keep some backward compatibility mappings

        case "Unknown":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#6C757D" }}>
              <QuestionMark fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        case "OutOfStock":
          return (
            <Box sx={{ ...boxStyle, bgcolor: "#6B7280" }}>
              <RemoveCircleIcon fontSize="small" sx={{ fontSize: 16 }} />
            </Box>
          );
        default:
          return <Box sx={boxStyle} />;
      }
    };

    // Determine max doses to show rows.
    // Format a slotTime string like "07:00" → "07:00 AM"
    const formatSlotTime = (slotTime: string): string => {
      if (!slotTime) return "";
      try {
        const [hours, minutes] = slotTime.split(":").map(Number);
        const d = new Date();
        d.setHours(hours, minutes || 0, 0, 0);
        return format(d, "hh:mm a");
      } catch {
        return slotTime;
      }
    };

    return (
      <>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
            ...sx,
          }}
        >
          {/* Status Legend */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              bgcolor: "white",
            }}
          >
            {[
              { label: "Taken", color: "#10B981", bg: "#D1FAE5", border: "#A7F3D0" },
              { label: "Missed", color: "#EF4444", bg: "#FEE2E2", border: "#FECACA" },
              { label: "Upcoming", color: "#8B5CF6", bg: "#EDE9FE", border: "#DDD6FE" },
              { label: "Refused", color: "#f06292", bg: "#fce4ec", border: "#f8bbd0" },
              { label: "Out of Stock", color: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB" },
              { label: "Discontinued", color: "#8B4513", bg: "#FFEDD5", border: "#FDBA74" },
              { label: "PRN", color: "#0EA5E9", bg: "#E0F2FE", border: "#BAE6FD" },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: item.bg,
                  border: `1px solid ${item.border}`,
                  borderRadius: "16px",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: "0.75rem" }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Header */}
          <Box
            sx={{
              bgcolor: "#48CAE4",
              color: "white",
              py: 2,
              px: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "1.1rem", letterSpacing: 0.5 }}
                >
                  MEDICATION ADMINISTRATION RECORD
                </Typography>
                {showResidentName && (
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "0.9rem", color: "white", fontWeight: 500 }}
                  >
                    Resident Name: {residentName}
                  </Typography>
                )}
              </Box>
              {/* Month navigation: < DatePicker > */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "white", borderRadius: 1, pr: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setCurrentDate(prev => subMonths(prev, 1))}
                  sx={{ color: "#555", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    views={["year", "month"]}
                    value={currentDate}
                    onChange={(newValue) => {
                      if (newValue) setCurrentDate(newValue);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          "& fieldset": { border: "none" },
                          width: 150,
                          "& .MuiInputBase-input": { fontWeight: "bold", px: 0 },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <IconButton
                  size="small"
                  onClick={() => setCurrentDate(prev => addMonths(prev, 1))}
                  sx={{ color: "#555", "&:hover": { bgcolor: "rgba(0,0,0,0.08)" } }}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box>
              {onClose && (
                <IconButton onClick={onClose} sx={{ color: "white", ml: 1 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Table */}
          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#888",
                borderRadius: "4px",
                "&:hover": {
                  bgcolor: "#555",
                },
              },
            }}
          >
            <Table size="small" sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 280,
                      borderRight: "1px solid #eee",
                      pl: 3,
                      py: 2,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      MEDICATION DETAILS
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 100,
                      borderRight: "1px solid #eee",
                      textAlign: "center",
                      py: 2,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      HOUR
                    </Typography>
                  </TableCell>
                  {/* Days Header */}
                  <TableCell
                    colSpan={daysInMonth.length}
                    sx={{
                      p: 0,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Box sx={{ display: "flex", borderBottom: "1px solid #eee" }}>
                      <Box sx={{ width: "100%", textAlign: "center", py: 0.5 }}>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          ADMINISTRATION RECORD
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      {daysInMonth.map((day) => (
                        <Box
                          key={day.toISOString()}
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            borderRight: "1px solid #eee",
                            py: 0.5,
                            minWidth: 32,
                            bgcolor: "white",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {format(day, "d")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={35} align="center" sx={{ py: 10 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : medicationRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={35} align="center" sx={{ py: 5 }}>
                      No medication records found for this month.
                    </TableCell>
                  </TableRow>
                ) : (
                  medicationRecords.map((record, index) => {
                    // Each slot is a separate row (doseNumber + slotTime)
                    const slots: MonthlyMARSlot[] = record.doses || [];

                    // Helper to parse various date formats safely
                    const parseNoteDate = (dateStr: any) => {
                      if (!dateStr) return 0;
                      const d = new Date(dateStr);
                      if (!isNaN(d.getTime())) return d.getTime();

                      // Handle DD/MM/YYYY
                      const match = String(dateStr).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                      if (match) {
                        return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1])).getTime();
                      }
                      return 0;
                    };

                    // Determine low-stock status from all records across all slots
                    const allLowStockStatuses = [
                      ...slots.flatMap(slot => slot.records.map(r => ({ date: r.dateTime || r.date || "", lowStock: r.lowStock }))),
                      ...nurseNotes
                        .filter(n => (n.medicationName === record.medicationName || n.medication === record.medicationName))
                        .map(n => ({ date: n.dateTime || n.noteDate || "", lowStock: n.lowStock }))
                    ].filter(item => item.lowStock !== undefined);

                    const latestStatus = allLowStockStatuses.sort((a, b) => parseNoteDate(b.date) - parseNoteDate(a.date))[0];
                    const isLowStock = latestStatus ? latestStatus.lowStock === true : false;

                    return (
                      <React.Fragment key={index}>
                        {slots.map((slot, doseIndex) => (
                          <TableRow key={`${record.medicationName}-${slot.doseNumber}-${slot.slotTime}`}>
                            {/* Medication Info Cell - Render only on first slot row */}
                            {doseIndex === 0 && (
                              <TableCell
                                rowSpan={slots.length || 1}
                                sx={{
                                  verticalAlign: "middle",
                                  borderRight: "1px solid #eee",
                                  pl: 3,
                                  py: 2,
                                  bgcolor: "#E1F5FE", // Light sky blue background
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    '&:hover .low-stock-icon': {
                                      opacity: 1,
                                      visibility: "visible"
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{ color: "#2c3e50", lineHeight: 1.2, display: "flex", alignItems: "center", gap: 1 }}
                                  >
                                    {record.medicationName}
                                    {isStatMedication(record) && (
                                      <Tooltip title="STAT Medication" arrow>
                                        <Box
                                          component="img"
                                          src="https://cdn-icons-png.flaticon.com/512/3791/3791036.png"
                                          alt="STAT dose"
                                          sx={{
                                            width: 12,
                                            height: 12,
                                            display: "block",
                                            filter: "invert(31%) sepia(86%) saturate(1637%) hue-rotate(174deg) brightness(97%) contrast(101%)",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {isNarcoticMedication(record) && (
                                      <Tooltip title="Narcotic Medication" arrow>
                                        <Box
                                          component="img"
                                          src="/assets/narcotic_icon.png"
                                          alt="Narcotic"
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            display: "block",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {isCriticalMedication(record) && (
                                      <Tooltip title="Critical Medication" arrow>
                                        <Box
                                          component="img"
                                          src="/assets/critical1.png"
                                          alt="Critical"
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            display: "block",
                                            transition: "transform 0.2s ease-in-out",
                                            "&:hover": {
                                              transform: "scale(1.15)",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Typography>
                                  {(() => {
                                    const isOutOfStock = slots.some(s => {
                                      const dose = findDoseInSlot(s, new Date());
                                      return dose?.status === "Out of Stock" || dose?.status === "OutOfStock" || dose?.status === "OutOfMeds";
                                    });

                                    if (isOutOfStock) {
                                      return (
                                        <Tooltip title="Out of Stock">
                                          <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <img
                                              src="/assets/Low-Stock-logo.png"
                                              alt="Out of Stock"
                                              style={{ height: 24, width: "auto" }}
                                            />
                                          </Box>
                                        </Tooltip>
                                      );
                                    }

                                    return (
                                      <Tooltip title={isLowStock ? "Low Stock" : "Mark as Low Stock"}>
                                        <Box
                                          className="low-stock-icon"
                                          sx={{
                                            opacity: isLowStock ? 1 : 0,
                                            visibility: isLowStock ? "visible" : "hidden",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center"
                                          }}
                                        >
                                          <img
                                            src="/assets/Low-Stock-logo.png"
                                            alt="Low Stock"
                                            style={{
                                              height: 24,
                                              width: "auto",
                                              cursor: readOnly ? "default" : "pointer",
                                              filter: isLowStock ? "none" : "grayscale(100%) opacity(0.3)",
                                            }}
                                            onClick={() => !readOnly && handleLowStockToggle(record, !isLowStock)}
                                          />
                                        </Box>
                                      </Tooltip>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                            )}

                            {/* Time Cell - directly uses slotTime from backend */}
                            <TableCell
                              sx={{
                                borderRight: "1px solid #eee",
                                textAlign: "center",
                                bgcolor: "#E1F5FE",
                                color: "#000",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                            >
                              {formatSlotTime(slot.slotTime)}
                            </TableCell>

                            {/* Grid Cells */}
                            <TableCell colSpan={daysInMonth.length} sx={{ p: 0 }}>
                              <Box sx={{ display: "flex" }}>
                                {daysInMonth.map((day) => {
                                  const doseData = findDoseInSlot(slot, day);
                                  const isPastDay = isBefore(day, startOfDay(new Date()));
                                  const isFutureDay = isFuture(day) && !isToday(day);
                                  let isPastTime = false;
                                  if (isToday(day) && slot.slotTime) {
                                    try {
                                      const [hours, minutes] = slot.slotTime.split(":").map(Number);
                                      const slotDateTime = new Date();
                                      slotDateTime.setHours(hours, minutes || 0, 0, 0);
                                      isPastTime = slotDateTime < new Date();
                                    } catch (e) {
                                      // ignore
                                    }
                                  }
                                  const isScheduled = !!doseData;
                                  const isDisabled = !isScheduled || isFutureDay || doseData?.status === "Taken";

                                  return (
                                    <Box
                                      key={day.toISOString()}
                                      onClick={() => !isDisabled && handleCellClick(record, slot, day, doseData)}
                                      sx={{
                                        flex: 1,
                                        minWidth: 32,
                                        height: 48,
                                        borderRight: "1px solid #f5f5f5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        transition: "all 0.2s",
                                        bgcolor: isDisabled ? "rgba(0,0,0,0.04)" : "transparent",
                                        "&:hover": {
                                          bgcolor: isDisabled ? "rgba(0,0,0,0.04)" : "rgba(72,202,228,0.08)",
                                          "& .cell-border": {
                                            borderColor: isDisabled ? "#eceff1" : "#48CAE4",
                                            transform: isDisabled ? "none" : "scale(1.1)",
                                            boxShadow: isDisabled ? "none" : "0 0 8px rgba(72,202,228,0.2)",
                                          }
                                        },
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          doseData ? (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.5,
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                {record.medicationName}
                                              </Typography>
                                              {doseData.dateTime && (
                                                <Typography variant="caption">
                                                  Date: {doseData.dateTime}
                                                </Typography>
                                              )}
                                              {doseData.date &&
                                                !doseData.dateTime && (
                                                  <Typography variant="caption">
                                                    Date: {doseData.date}
                                                  </Typography>
                                                )}
                                              <Typography variant="caption">
                                                Response: {(() => {
                                                  const realStatus = doseData?.status && doseData.status !== "Upcoming" ? doseData.status : null;
                                                  return realStatus || ((isPastDay || isPastTime) ? "Missed" : (doseData?.status || "N/A"));
                                                })()}
                                              </Typography>
                                              {doseData.administeredBy && (
                                                <Typography variant="caption">
                                                  Caregiver:{" "}
                                                  {doseData.administeredBy}
                                                </Typography>
                                              )}
                                              {doseData.administeredAt && (
                                                <Typography variant="caption">
                                                  Time: {doseData.administeredAt}
                                                </Typography>
                                              )}
                                              {doseData.notes && (
                                                <Typography variant="caption">
                                                  Notes: {doseData.notes}
                                                </Typography>
                                              )}
                                              {(doseData.updatedByName || doseData.createdByName) &&
                                                (doseData.updatedByName !== "-" || doseData.createdByName !== "-") && (
                                                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    👤 Caregiver: {doseData.updatedByName || doseData.createdByName}
                                                  </Typography>
                                                )}
                                            </Box>
                                          ) : (
                                            "No status"
                                          )
                                        }
                                        arrow
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: "rgba(0, 0, 0, 0.87)",
                                              color: "#fff",
                                              boxShadow:
                                                "0 2px 8px rgba(0,0,0,0.15)",
                                              fontSize: "0.75rem",
                                              padding: "8px 12px",
                                            },
                                          },
                                        }}
                                      >
                                        <Box>
                                          {(() => {
                                            // "Upcoming" from backend = not yet administered → treat as no real status
                                            const realStatus = doseData?.status && doseData.status !== "Upcoming"
                                              ? doseData.status
                                              : null;

                                            if (!doseData) {
                                              // Outside medication date range → grey cross
                                              return (
                                                <CloseIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                              );
                                            }
                                            if (realStatus) {
                                              // Has a real recorded status (Taken, Missed, Refused, etc.)
                                              return renderStatusIcon(realStatus, doseData.colorCode);
                                            }
                                            if (isPastDay || isPastTime) {
                                              // Scheduled, past, not administered → auto Missed
                                              return renderStatusIcon(isStatDoseSource(record) ? "Not Given" : "Missed");
                                            }
                                            // Scheduled, today or future, not yet administered → Upcoming
                                            // STAT dose medications should NOT appear as Upcoming
                                            if (isStatDoseSource(record)) {
                                              return (
                                                <CloseIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                              );
                                            }
                                            return renderStatusIcon("Upcoming");
                                          })()}
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Details Row */}
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderRight: "1px solid #eee",
                              pl: 3,
                              py: 1,
                              bgcolor: "white",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                <Box component="span" fontWeight="bold">
                                  Medication Start Date:
                                </Box>{" "}
                                {formatToUIDate(
                                  record.startDate ||
                                  (record as any).start ||
                                  (record as any).start_date
                                )}{" "}
                                <Box component="span" fontWeight="bold">
                                  {" "}
                                  - Refill Date:
                                </Box>{" "}
                                {formatToUIDate(
                                  record.endDate ||
                                  (record as any).end ||
                                  (record as any).end_date ||
                                  (record as any).nextRefillDate
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Dose:</Box> {record.dose || "N/A"} |{" "}
                                <Box component="span" fontWeight="bold">Route:</Box>{" "}
                                {record.route || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Schedule:</Box> {record.schedule || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.85rem", color: "#555" }}
                              >
                                <Box component="span" fontWeight="bold">Stock:</Box> {record.updatedStock ?? (record as any).updated_stock ?? "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* Empty Grid Cells for formatting continuity */}
                          <TableCell colSpan={daysInMonth.length} sx={{ p: 0 }}>
                            <Box sx={{ display: "flex" }}>
                              {daysInMonth.map((day) => (
                                <Box
                                  key={`empty-${day.toISOString()}`}
                                  sx={{
                                    flex: 1,
                                    minWidth: 32,
                                    height: 48, // Match height of dose row? Or smaller?
                                    // Image suggests detail row might be slightly shorter or same.
                                    // Let's use auto logic or standard height.
                                    borderRight: "1px solid #f5f5f5",
                                    bgcolor: "#fafafa", // Slight grey for empty area? Or white? Image looked white or empty.
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Actions */}
          {onClose && (
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  bgcolor: "#0f172a",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { bgcolor: "#1e293b" },
                }}
              >
                Close Record
              </Button>
            </Box>
          )}
          {/* Status Selection Dialog */}
          <Dialog
            open={isStatusDialogOpen}
            onClose={() => { if (!isSavingStatus) { setIsStatusDialogOpen(false); setFormError(null); } }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3, overflow: 'hidden' }
            }}
          >
            <DialogTitle sx={{
              bgcolor: '#48CAE4',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleIcon />
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                  Record Administration
                </Typography>
              </Box>
              <IconButton
                onClick={() => setIsStatusDialogOpen(false)}
                size="small"
                sx={{ color: 'white' }}
                disabled={isSavingStatus}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {selectedCell && (
                <Box sx={{ p: 3 }}>
                  {/* Inline validation error – shown at top of dialog */}
                  {formError && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.88rem',
                      }}
                    >
                      {formError}
                    </Alert>
                  )}
                  {/* Medication Info Card */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ p: 1, bgcolor: 'rgba(72,202,228,0.1)', borderRadius: 1.5, color: '#48CAE4' }}>
                        <LocalPharmacyIcon />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="#1e293b" sx={{ lineHeight: 1.2, mb: 0.5, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                          {selectedCell.record.medicationName}
                          {isStatMedication(selectedCell.record) && (
                            <Box
                              component="img"
                              src="https://cdn-icons-png.flaticon.com/512/3791/3791036.png"
                              alt="STAT dose"
                              sx={{
                                width: 14,
                                height: 14,
                                display: "inline-block",
                                verticalAlign: "middle",
                                filter: "invert(31%) sepia(86%) saturate(1637%) hue-rotate(174deg) brightness(97%) contrast(101%)"
                              }}
                            />
                          )}
                          <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>
                            | Caregiver: {user?.name || user?.username || 'N/A'}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <InfoIcon sx={{ fontSize: 14 }} /> {selectedCell.record.dose || 'N/A'} • {selectedCell.record.schedule || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          <Typography variant="body2" fontWeight="medium" color="#334155">
                            {format(selectedCell.day, 'EEEE, MMM do')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HistoryIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          <TextField
                            type="time"
                            size="small"
                            value={administrationTime}
                            onChange={(e) => setAdministrationTime(e.target.value)}
                            sx={{
                              width: 120,
                              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' },
                              '& fieldset': { border: 'none' },
                              bgcolor: '#f1f5f9',
                              borderRadius: 2
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 10 }}>
                        <InventoryIcon sx={{ fontSize: 16, color: '#48CAE4' }} />
                        <Typography variant="caption" fontWeight="bold" color="#48CAE4">
                          Stock: {selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock ?? "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 1.5, px: 0.5 }}>
                    Results / Response {
                      (selectedCell && (checkIsReasonMandatory(selectedCell.day, selectedCell.slot.slotTime, selectedCell.doseData?.dateTime) || dialogStatus === 'Missed' || dialogStatus === 'NotTaken' || dialogStatus === 'Unable')) && (
                        <span style={{ color: '#DC3545' }}>*</span>
                      )
                    }
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter any results or response notes from daily medication..."
                    value={resultsResponse}
                    onChange={(e) => {
                      setResultsResponse(e.target.value);
                      if (formError) setFormError(null);
                    }}
                  />


                  <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 2, px: 0.5 }}>
                    Status
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    {[
                      { label: 'Taken', value: 'Taken', color: '#28A745', icon: <CheckCircleIcon /> },
                      { label: 'Missed', value: 'Missed', color: '#DC3545', icon: <CancelCircleIcon /> },
                      { label: 'Refused', value: 'Refused', color: '#7c4dff', icon: <Block /> },
                      { label: 'Out of Stock', value: 'OutOfMeds', color: '#1e293b', icon: <InventoryIcon /> },
                      { label: 'Discontinued', value: 'Discontinued', color: '#795548', icon: <RemoveCircleIcon /> },
                      { label: 'PRN', value: 'PRN', color: '#0288d1', icon: <LocalPharmacyIcon /> },
                    ].map((status) => {
                      const currentStock = selectedCell.record.updatedStock ?? (selectedCell.record as any).updated_stock ?? "N/A";
                      const isStockAvailable = currentStock !== "N/A" && currentStock !== null && currentStock !== undefined && Number(currentStock) > 0;
                      const isSelected = dialogStatus === status.value;

                      return (
                        <Button
                          key={status.value}
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => {
                            setDialogStatus(status.value);
                            if ((status.value === 'Unable' || status.value === 'Missed') && !resultsResponse.trim()) {
                              setFormError(`Results / Response is required for ${status.label}.`);
                              return;
                            }
                            handleRecordStatus(status.value);
                          }}
                          disabled={
                            isSavingStatus ||
                            (status.label === "Out of Stock" && isStockAvailable)
                          }
                          sx={{
                            py: 1.5,
                            px: 2,
                            borderRadius: 2,
                            bgcolor: isSelected ? status.color : 'transparent',
                            border: `1.5px solid ${status.color}${isSelected ? '' : '20'}`,
                            color: isSelected ? 'white' : status.color,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: isSelected ? status.color : `${status.color}10`,
                              border: `1.5px solid ${status.color}`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${status.color}15`,
                            }
                          }}
                        >
                          {status.icon}
                          <Typography variant="body2" fontWeight="bold">
                            {status.label}
                          </Typography>
                        </Button>
                      );
                    })}
                  </Box>

                  {/* Inline Narcotic Signature Area */}
                  {pendingStatus === 'Taken' && (
                    <Box sx={{ mt: 4, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="#1e293b">
                          Narcotic Signature Verification
                        </Typography>
                        <Chip label="Required" color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                      </Box>

                      <Tabs
                        value={signatureTab}
                        onChange={(_: any, v: number) => setSignatureTab(v)}
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                      >
                        <Tab label="Draw Signature" icon={<EditIcon />} iconPosition="start" />
                        <Tab label="Upload Image" icon={<CloudUploadIcon />} iconPosition="start" />
                      </Tabs>

                      {signatureTab === 0 ? (
                        <Box sx={{ py: 1 }}>
                          <canvas
                            ref={canvasRef}
                            width={400}
                            height={150}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            style={{
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              cursor: 'crosshair',
                              display: 'block',
                              margin: '0 auto',
                              backgroundColor: '#ffffff',
                              maxWidth: '100%'
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button size="small" onClick={clearCanvas} sx={{ textTransform: 'none' }}>
                              Clear Drawing
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                          >
                            Choose Signature File
                          </Button>
                          {signatureImage && (
                            <Box sx={{ mt: 2 }}>
                              <img
                                src={signatureImage}
                                alt="Signature Preview"
                                style={{ maxWidth: '100%', maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {pendingStatus === 'Taken' && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Button
                        onClick={() => {
                          setIsStatusDialogOpen(false);
                          setPendingStatus(null);
                        }}
                        disabled={isSavingStatus}
                        sx={{ textTransform: 'none', fontWeight: 'bold' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (!signatureFile) {
                            setFormError("Signature is mandatory for narcotic medications.");
                            return;
                          }
                          submitRecord('Taken', signatureFile);
                        }}
                        disabled={isSavingStatus}
                        sx={{
                          borderRadius: 2,
                          px: 4,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #0066cc 0%, #00478f 100%)',
                          '&:disabled': { opacity: 0.7 }
                        }}
                      >
                        {isSavingStatus ? <CircularProgress size={24} color="inherit" /> : 'Record Administration'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            {isSavingStatus && (
              <Box sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <CircularProgress size={40} thickness={4} sx={{ color: '#48CAE4' }} />
              </Box>
            )}
          </Dialog>
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  };

export default MedicationAdministrationRecord;